// src/GaloBlogPage.jsx - integrated with dynamic blog images and related articles
import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronRight,
  Search,
  ChevronLeft,
  Clock,
  Calendar,
  Filter,
  ChevronDown,
  ArrowUp,
  Mail,
  CheckCircle,
  TrendingUp,
  Sparkles
} from 'lucide-react';

// Import layout components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Import blog data utilities
import { 
  getBlogCategories, 
  getFeaturedPosts, 
  getPostsByCategory, 
  getRelatedPosts,
  getRecentPosts 
} from './data/blogPosts';
import { setDocumentMeta } from './utils/documentMeta';

// 🎯 Import the dynamic blog image component
import DynamicBlogImage from './components/sections/DynamicBlogImage';

const GaloBlogPage = () => {
  // Get category from URL if present
  const { category: urlCategory } = useParams();
  
  // State management
  const [scrolled, setScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState(urlCategory || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  
  // Update active category when URL changes
  useEffect(() => {
    if (urlCategory) {
      setActiveCategory(urlCategory);
      setCurrentPage(1);
    }
  }, [urlCategory]);
  
  // Refs for animations
  const heroRef = useRef(null);
  const postsRef = useRef(null);
  const relatedRef = useRef(null);
  const newsletterRef = useRef(null);

  // Get blog data
  const categories = getBlogCategories();
  const featuredPost = getFeaturedPosts(1)[0];
  const allBlogPosts = getPostsByCategory(activeCategory);
  
  // Get related/trending posts for the related articles section
  const relatedPosts = activeCategory !== 'all' 
    ? getRelatedPosts(featuredPost?.id || '', activeCategory, 4)
    : getRecentPosts(true, 4); // Get recent posts excluding featured

  // Pagination
  const postsPerPage = 6;
  const totalPages = Math.ceil(allBlogPosts.length / postsPerPage);
  const paginatedPosts = allBlogPosts.slice(
    (currentPage - 1) * postsPerPage, 
    currentPage * postsPerPage
  );

  // Set document title and meta
  useEffect(() => {
    const title = activeCategory !== 'all' 
      ? `${categories.find(c => c.id === activeCategory)?.label} | Blog | Galo Industries`
      : 'Blog | Galo Industries';
      
    setDocumentMeta(
      title, 
      "Industry insights, technical guides, and automation trends for modern manufacturing"
    );
    
    return () => {
      setDocumentMeta('Galo Industries', 'Default description');
    };
  }, [activeCategory, categories]);

  // Handle pagination
  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({
      top: document.getElementById('blog-posts').offsetTop - 100,
      behavior: 'smooth'
    });
  };

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Handle category change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentPage(1);
    setShowFiltersMobile(false);
  };
  
  // Handle newsletter subscription
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter signup:', newsletterEmail);
    setNewsletterEmail('');
    alert('Thank you for subscribing to our newsletter!');
  };

  // Animation on scroll with Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -100px 0px" }
    );
    
    const refs = [heroRef, postsRef, relatedRef, newsletterRef];
    
    refs.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });
    
    return () => {
      refs.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  return (
    <div className="font-sans antialiased overflow-x-hidden bg-white">
      {/* Global Styles */}
      <style jsx global>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 0.8; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse 4s ease-in-out infinite;
        }
        
        /* Scroll animation classes */
        .hero-content,
        .posts-grid,
        .related-section,
        .newsletter-section {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        
        .is-visible .hero-content,
        .is-visible .posts-grid,
        .is-visible .related-section,
        .is-visible .newsletter-section {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Blog card animations */
        .blog-card {
          opacity: 0;
          transform: translateY(20px);
          animation: fadeIn 0.5s forwards;
        }
        
        .is-visible .blog-card:nth-child(1) { animation-delay: 0.1s; }
        .is-visible .blog-card:nth-child(2) { animation-delay: 0.2s; }
        .is-visible .blog-card:nth-child(3) { animation-delay: 0.3s; }
        .is-visible .blog-card:nth-child(4) { animation-delay: 0.4s; }
        .is-visible .blog-card:nth-child(5) { animation-delay: 0.5s; }
        .is-visible .blog-card:nth-child(6) { animation-delay: 0.6s; }
        
        /* Related articles animations */
        .related-card {
          opacity: 0;
          transform: translateY(15px);
          animation: fadeIn 0.4s forwards;
        }
        
        .is-visible .related-card:nth-child(1) { animation-delay: 0.1s; }
        .is-visible .related-card:nth-child(2) { animation-delay: 0.2s; }
        .is-visible .related-card:nth-child(3) { animation-delay: 0.3s; }
        .is-visible .related-card:nth-child(4) { animation-delay: 0.4s; }
      `}</style>
      
      {/* Use Header component from layout */}
      <Header activeSection="blog" />
      
      {/* Blog Hero Section */}
      <section 
        ref={heroRef}
        className="pt-32 pb-16 bg-gradient-to-br from-cyan-600 via-blue-700 to-blue-900 text-white relative overflow-hidden"
      >
        {/* Background decorations */}
        <div className="absolute inset-0 bg-[radial-gradient(circle,_rgba(255,255,255,0.15)_1px,_transparent_1px)] bg-[length:20px_20px]"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-cyan-400/10 to-blue-300/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-teal-500/10 to-blue-300/5 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}}></div>
        
        {/* Gradient border bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-cyan-500 to-blue-600 opacity-20"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          {/* Breadcrumb */}
          <div className="mb-8">
            <div className="flex items-center text-blue-100 text-sm">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white font-medium">Blog</span>
              {activeCategory !== 'all' && (
                <>
                  <ChevronRight className="w-4 h-4 mx-2" />
                  <span className="text-white font-medium">
                    {categories.find(c => c.id === activeCategory)?.label}
                  </span>
                </>
              )}
            </div>
          </div>
          
          <div className="hero-content">
            {/* Simplified Centered Hero Content */}
            <div className="text-center max-w-4xl mx-auto mb-12">
              <div className="inline-block bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20 shadow-md mb-6">
                <span className="flex items-center text-white text-sm font-medium">
                  <span className="inline-block w-2 h-2 rounded-full bg-cyan-400 mr-2 animate-pulse"></span>
                  AUTOMATION INSIGHTS
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {activeCategory !== 'all' 
                  ? `${categories.find(c => c.id === activeCategory)?.label} Articles`
                  : <>Industry Knowledge & <span className="text-cyan-200">Innovation</span></>
                }
              </h1>
              
              <p className="text-xl text-blue-100 mb-10 mx-auto max-w-2xl">
                Expert perspectives, trends, and technologies shaping the future of industrial automation and manufacturing
              </p>
            </div>
            
            {/* Featured Article with Dynamic Image */}
            {featuredPost && activeCategory === 'all' && (
              <div className="bg-white/10 backdrop-blur-sm rounded-xl overflow-hidden border border-white/20 shadow-xl mb-8">
                <div className="md:flex">
                  {/* 🎯 UPDATED: Featured image with dynamic template */}
                  <div className="md:w-1/2 relative">
                    <DynamicBlogImage
                      post={featuredPost}
                      className="w-full h-64 md:h-full"
                    />
                    
                    {/* Featured badge overlay */}
                    <div className="absolute top-4 left-4">
                      <div className="px-3 py-1 bg-yellow-400 text-slate-900 text-xs font-bold rounded-md shadow-lg">
                        Featured
                      </div>
                    </div>
                  </div>
                  
                  {/* Featured content */}
                  <div className="md:w-1/2 p-6 md:p-8">
                    <h2 className="text-2xl font-bold text-white mb-3">{featuredPost.title}</h2>
                    
                    <p className="text-blue-100 mb-6 line-clamp-3">{featuredPost.excerpt}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-sm text-blue-200">
                        <Calendar className="w-4 h-4 mr-1" />
                        {featuredPost.date}
                        <Clock className="w-4 h-4 ml-4 mr-1" />
                        {featuredPost.readTime}
                      </div>
                      
                      <Link 
                        to={`/blog/${featuredPost.slug}`}
                        className="inline-flex items-center px-4 py-2 bg-white text-blue-700 hover:bg-blue-50 rounded-lg shadow-md transition-all text-sm font-medium"
                      >
                        Read Article
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Call to action */}
            <div className="text-center">
              <a 
                href="#blog-posts"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-blue-600/20 rounded-lg shadow-md transition-all font-medium"
              >
                Explore All Articles
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
      
      {/* Blog Posts Section */}
      <section id="blog-posts" className="py-20" ref={postsRef}>
        <div className="container mx-auto px-6">
          {/* Section header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center mb-3">
              <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
              <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3">Our Articles</span>
              <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Latest Industry <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Insights</span>
            </h2>
            <p className="text-slate-600">
              Explore our collection of articles covering automation, engineering, and manufacturing technology
            </p>
          </div>
          
          {/* Category filters */}
          <div className="mb-12">
            <div className="flex justify-center">
              <div className="inline-flex flex-wrap bg-white border border-slate-200 rounded-lg shadow-md p-1.5">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-5 py-2.5 text-sm font-medium transition-colors whitespace-nowrap rounded-lg m-1 ${
                      activeCategory === category.id
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md' 
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {category.label}
                    {category.count && (
                      <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full inline-block ${
                        activeCategory === category.id
                          ? 'bg-white/20' 
                          : 'bg-slate-200'
                      }`}>
                        {category.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Mobile category filter */}
          <div className="sm:hidden mb-8">
            <button
              onClick={() => setShowFiltersMobile(!showFiltersMobile)}
              className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-lg shadow-md text-slate-700"
            >
              <span className="flex items-center">
                <Filter className="w-4 h-4 mr-2" />
                {categories.find(c => c.id === activeCategory)?.label || 'All Categories'}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
            
            {showFiltersMobile && (
              <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-lg p-3 absolute z-20 left-6 right-6">
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryChange(category.id)}
                      className={`px-3 py-2.5 text-sm font-medium transition-colors rounded-lg ${
                        activeCategory === category.id
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Blog post grid with Dynamic Images */}
          {paginatedPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12 posts-grid">
              {paginatedPosts.map((post, index) => (
                <article 
                  key={post.id}
                  className="blog-card group bg-white rounded-xl overflow-hidden shadow-md border border-slate-100 flex flex-col transition-all duration-300 hover:shadow-xl hover:border-cyan-100 hover:-translate-y-1"
                >
                  {/* 🎯 UPDATED: Blog post image with dynamic template */}
                  <div className="relative overflow-hidden">
                    <DynamicBlogImage
                      post={post}
                      className="w-full h-48"
                    />
                    
                    {/* Featured badge if applicable */}
                    {post.featured && (
                      <div className="absolute top-4 right-4">
                        <div className="px-3 py-1 bg-yellow-400 text-slate-900 text-xs font-bold rounded-md shadow-lg">
                          Featured
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="flex items-center text-xs text-slate-500 mb-3">
                      <Calendar className="w-3.5 h-3.5 mr-1 text-cyan-600" />
                      <span className="mr-4">{post.date}</span>
                      <Clock className="w-3.5 h-3.5 mr-1 text-cyan-600" />
                      <span>{post.readTime}</span>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-cyan-700 transition-colors line-clamp-2">
                      <Link to={`/blog/${post.slug}`} className="block">
                        {post.title}
                      </Link>
                    </h3>
                    
                    <p className="text-slate-600 mb-5 line-clamp-3 flex-grow">
                      {post.excerpt}
                    </p>
                    
                    <div className="pt-4 border-t border-slate-100 mt-auto">
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="inline-flex items-center text-cyan-700 font-medium hover:text-blue-700 transition-colors group text-sm"
                      >
                        Read More
                        <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 px-4 bg-white rounded-xl shadow-md">
              <div className="mb-6 inline-block p-3 bg-cyan-50 rounded-full">
                <Search className="w-6 h-6 text-cyan-700" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">No articles found</h3>
              <p className="text-slate-600 mb-6">
                We couldn't find any articles in this category.
              </p>
              <button 
                onClick={() => {
                  setActiveCategory('all');
                }}
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:shadow-lg hover:shadow-cyan-500/20 transition-all"
              >
                View All Articles
              </button>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-10">
              <button
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  currentPage === 1
                    ? 'text-slate-400 bg-slate-100 cursor-not-allowed'
                    : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index + 1)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  currentPage === index + 1
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium'
                      : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
              
              <button
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  currentPage === totalPages
                    ? 'text-slate-400 bg-slate-100 cursor-not-allowed'
                    : 'text-slate-700 bg-white hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* 🎯 NEW: Related Articles Section with Dynamic Images */}
      {relatedPosts.length > 0 && (
        <section ref={relatedRef} className="py-16 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-full opacity-60 blur-3xl -z-10"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-tr from-blue-50 to-cyan-50 rounded-full opacity-60 blur-3xl -z-10"></div>
          
          <div className="container mx-auto px-6">
            <div className="related-section">
              {/* Section header */}
              <div className="text-center max-w-3xl mx-auto mb-12">
                <div className="inline-flex items-center mb-4">
                  <div className="h-px w-12 bg-gradient-to-r from-cyan-500 to-blue-600"></div>
                  <span className="text-cyan-700 font-semibold uppercase text-sm tracking-wider mx-3 flex items-center">
                    {activeCategory !== 'all' ? (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Related Articles
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Trending Now
                      </>
                    )}
                  </span>
                  <div className="h-px w-12 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
                </div>
                
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  {activeCategory !== 'all' ? (
                    <>More <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">{categories.find(c => c.id === activeCategory)?.label}</span> Articles</>
                  ) : (
                    <>What's <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Trending</span></>
                  )}
                </h2>
                
                <p className="text-slate-600">
                  {activeCategory !== 'all' 
                    ? `Discover more insights about ${categories.find(c => c.id === activeCategory)?.label.toLowerCase()}`
                    : 'The most popular articles from our automation and manufacturing experts'
                  }
                </p>
              </div>

              {/* Related articles grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedPosts.map((post, index) => (
                  <article 
                    key={post.id}
                    className="related-card group bg-white rounded-xl overflow-hidden shadow-md border border-slate-100 flex flex-col transition-all duration-300 hover:shadow-xl hover:border-cyan-100 hover:-translate-y-1"
                  >
                    {/* Dynamic image */}
                    <div className="relative overflow-hidden">
                      <DynamicBlogImage
                        post={post}
                        className="w-full h-40"
                      />
                      
                      {/* Trending indicator for trending posts */}
                      {activeCategory === 'all' && index === 0 && (
                        <div className="absolute top-3 left-3">
                          <div className="flex items-center px-2 py-1 bg-gradient-to-r from-orange-400 to-red-500 text-white text-xs font-bold rounded-full shadow-lg">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            Hot
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4 flex flex-col flex-grow">
                      <div className="flex items-center text-xs text-slate-500 mb-2">
                        <Calendar className="w-3 h-3 mr-1 text-cyan-600" />
                        <span className="mr-3">{post.date}</span>
                        <Clock className="w-3 h-3 mr-1 text-cyan-600" />
                        <span>{post.readTime}</span>
                      </div>
                      
                      <h3 className="text-lg font-bold mb-2 text-slate-900 group-hover:text-cyan-700 transition-colors line-clamp-2 flex-grow">
                        <Link to={`/blog/${post.slug}`} className="block">
                          {post.title}
                        </Link>
                      </h3>
                      
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      
                      <div className="pt-3 border-t border-slate-100 mt-auto">
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="inline-flex items-center text-cyan-700 font-medium hover:text-blue-700 transition-colors text-sm"
                        >
                          Read More
                          <ArrowRight className="ml-1 w-3 h-3 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* View more button */}
              <div className="text-center mt-10">
                <Link 
                  to={activeCategory !== 'all' ? `/blog/category/${activeCategory}` : '/blog'}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-blue-600/20 rounded-lg shadow-md transition-all font-medium"
                >
                  {activeCategory !== 'all' ? `View All ${categories.find(c => c.id === activeCategory)?.label} Articles` : 'View All Articles'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* Newsletter Section */}
      <section ref={newsletterRef} className="py-16 bg-gradient-to-br from-cyan-50 to-blue-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-100/50 to-transparent rounded-bl-full opacity-60 transform translate-x-1/4 -translate-y-1/4"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tl from-blue-100/50 to-transparent rounded-tr-full opacity-60 transform -translate-x-1/4 translate-y-1/4"></div>
        
        <div className="container mx-auto px-6 relative">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl p-6 sm:p-10 border border-slate-100 relative overflow-hidden newsletter-section">
            <div className="absolute inset-0 bg-[radial-gradient(#e1ebf8_1px,transparent_1px)] bg-[length:12px_12px] opacity-30 -z-10"></div>
            
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
                <Mail className="w-8 h-8" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-slate-900">Stay Updated with <span className="text-cyan-700">Industry Trends</span></h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Subscribe to our newsletter for the latest automation insights, case studies, and technology updates
              </p>
            </div>
            
            <form onSubmit={handleNewsletterSubmit} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors shadow-sm"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-lg hover:shadow-blue-600/20 text-white font-medium rounded-xl shadow-md transition-all"
                >
                  Subscribe
                </button>
              </div>
              <div className="text-center mt-4 text-sm text-slate-500 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 mr-2 text-cyan-700" />
                We respect your privacy. Unsubscribe at any time.
              </div>
            </form>
          </div>
        </div>
      </section>
      
      {/* Use Footer component from layout */}
      <Footer />
      
      {/* Back to top button */}
      {scrolled && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl shadow-lg flex items-center justify-center hover:-translate-y-1 transition-all duration-300 z-20 hover:shadow-xl hover:shadow-blue-600/20"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default GaloBlogPage;